import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setLoading(true);
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Look out for our quarterly note.");
      setEmail("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.message("You're already on the list.");
      else toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#1B3B2B] text-[#FDFBF7]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-10 grid place-items-center bg-[#D4AF37] text-[#1B3B2B] font-serif-display text-xl leading-none">
                A
              </div>
              <div className="leading-tight flex-1">
                <div className="font-serif-display text-xl">
                  Anand Aditya <span className="text-[#D4AF37]">&amp;</span> Co.
                </div>
                <div className="overline text-[10px] mt-0.5 text-[#D4AF37]">
                  Chartered Accountants
                </div>
              </div>
              <img
                src="https://customer-assets.emergentagent.com/job_chartered-anand/artifacts/jpojo6ch_logo_download-new.jpg"
                alt="CA India · ICAI member firm"
                className="h-12 w-12 object-contain bg-[#FDFBF7] p-1 rounded-sm"
                data-testid="footer-ca-india-logo"
              />
            </div>
            <p className="text-[#D8D2C2] max-w-md leading-relaxed text-sm">
              A multi-disciplinary chartered accountancy practice serving promoters, founders and
              family businesses across direct tax, indirect tax, audit and regulatory compliance.
            </p>

            <form onSubmit={subscribe} data-testid="newsletter-form" className="mt-10 max-w-md">
              <div className="overline text-[10px] mb-3 text-[#D4AF37]">/ Quarterly Note</div>
              <div className="flex gap-0 border border-[#D4AF37]/40">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="flex-1 bg-transparent px-4 py-3 text-sm text-[#FDFBF7] placeholder:text-[#9CAA9F] outline-none"
                />
                <button
                  data-testid="newsletter-submit"
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#1B3B2B] px-5 text-xs font-medium disabled:opacity-60 hover:bg-[#B3932E] transition-colors"
                >
                  {loading ? "…" : "Subscribe"}
                </button>
              </div>
            </form>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-[10px] mb-5 text-[#D4AF37]">Practice</div>
            <ul className="space-y-3 text-sm">
              {["services", "about", "team", "insights", "contact"].map((id) => (
                <li key={id}>
                  <button
                    data-testid={`footer-nav-${id}`}
                    onClick={() => scrollTo(id)}
                    className="text-[#D8D2C2] hover:text-[#D4AF37] transition-colors capitalize"
                  >
                    {id}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="overline text-[10px] mb-5 text-[#D4AF37]">Services</div>
            <ul className="space-y-3 text-sm text-[#D8D2C2]">
              <li>Income Tax</li>
              <li>GST</li>
              <li>Audit</li>
              <li>ROC &amp; MCA</li>
              <li>Advisory</li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <div className="overline text-[10px] mb-5 text-[#D4AF37]">Office</div>
            <div className="text-sm text-[#D8D2C2] leading-relaxed">
              #113, 2nd Floor, 100 ft Ring Road,
              <br />
              5th Block, 3rd Phase,
              <br />
              Banashankari 3rd Stage,
              <br />
              Bengaluru, Karnataka 560085
            </div>
            <div className="mt-6 text-sm">
              <a href="tel:+917829401779" className="block text-[#FDFBF7] hover:text-[#D4AF37]">
                +91 78294 01779
              </a>
              <a
                href="mailto:aditya@caadityakumar.com"
                className="block text-[#FDFBF7] hover:text-[#D4AF37]"
              >
                aditya@caadityakumar.com
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#D4AF37]/30 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="overline text-[10px] text-[#9CAA9F]">
            © {new Date().getFullYear()} Anand Aditya &amp; Co.
          </div>
          <div className="overline text-[10px] text-[#9CAA9F]">
            ICAI Registered Firm · All filings reviewed by partner before submission
          </div>
        </div>
      </div>
    </footer>
  );
}
